import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { propertiesAPI } from '../api';
import { Home, BedDouble, MapPin, ArrowLeft, ExternalLink, Share2, Phone, Mail, Calendar, Sofa, Building2, Facebook, Twitter, MessageCircle } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = '/property-placeholder.jpg';

const fmtPrice = (n) => (typeof n === 'number' && n > 0 ? `£${n.toLocaleString()} pcm` : 'Price on request');
const resolveImg = (url) => {
  if (!url) return `${BACKEND_URL}${FALLBACK_IMAGE}`;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const PropertyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    propertiesAPI
      .bySlug(slug)
      .then((r) => setProp(r.data))
      .catch(() => setError('not-found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = prop?.title || 'Property to let in Paisley';
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (_) { /* user cancelled */ }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied', description: 'Share it with friends or family.' });
      } catch (_) { /* clipboard blocked */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !prop) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Property not found</h1>
          <p className="text-gray-600 mb-6">This listing may have expired or been removed.</p>
          <button onClick={() => navigate('/properties')} className="text-emerald-600 font-medium">
            ← Back to all properties
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const description = prop.description || `${prop.property_type === 'house' ? 'House' : 'Flat'} to let in ${prop.area || 'Paisley'}${prop.monthly_rent ? `, ${fmtPrice(prop.monthly_rent)}` : ''}. Listed via ${prop.agent || prop.source_site || 'local agent'}.`;
  const safeDesc = DOMPurify.sanitize(
    description.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join(''),
    { ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li'], ALLOWED_ATTR: ['href', 'target', 'rel'] },
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: prop.title,
    description,
    url: typeof window !== 'undefined' ? window.location.href : `https://paisley.org.uk/properties/${prop.slug}`,
    image: prop.image_url ? [resolveImg(prop.image_url)] : undefined,
    datePosted: prop.created_at,
    address: {
      '@type': 'PostalAddress',
      streetAddress: prop.address || undefined,
      addressLocality: prop.area || 'Paisley',
      addressRegion: 'Renfrewshire',
      addressCountry: 'GB',
    },
    numberOfBedrooms: prop.bedrooms || undefined,
    offers: prop.monthly_rent ? {
      '@type': 'Offer',
      price: prop.monthly_rent,
      priceCurrency: 'GBP',
      priceSpecification: { '@type': 'UnitPriceSpecification', price: prop.monthly_rent, priceCurrency: 'GBP', unitText: 'MONTH' },
    } : undefined,
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${prop.title} | Paisley Properties to Let`}
        description={description.slice(0, 160)}
        keywords={`Paisley flat to let, ${prop.area}, ${prop.property_type}, ${prop.bedrooms || ''} bedroom, Renfrewshire rental, ${prop.agent}`}
        image={prop.image_url ? resolveImg(prop.image_url) : null}
        url={`/properties/${prop.slug}`}
        type="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />

      {/* Hero gallery */}
      <section className="relative bg-slate-900">
        <div className="absolute inset-0 opacity-30">
          <img
            src={resolveImg(prop.image_url)}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { if (!e.target.dataset.f) { e.target.dataset.f = '1'; e.target.src = `${BACKEND_URL}${FALLBACK_IMAGE}`; } }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-14">
          <Link to="/properties" className="inline-flex items-center gap-1 text-emerald-300 text-sm mb-6 hover:text-emerald-200">
            <ArrowLeft size={14} /> Back to all properties
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs uppercase font-bold tracking-wider text-emerald-200 bg-emerald-500/20 backdrop-blur px-3 py-1 rounded-full">
              {prop.property_type}
            </span>
            {!prop.active && (
              <span className="text-xs uppercase font-bold tracking-wider text-amber-200 bg-amber-500/20 backdrop-blur px-3 py-1 rounded-full">
                Expired
              </span>
            )}
            {prop.featured && (
              <span className="text-xs uppercase font-bold tracking-wider text-fuchsia-200 bg-fuchsia-500/20 backdrop-blur px-3 py-1 rounded-full">
                ★ Featured
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight" data-testid="property-detail-title">
            {prop.title}
          </h1>
          <p className="text-3xl font-extrabold text-emerald-300 mb-2">{fmtPrice(prop.monthly_rent)}</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-200 mt-3">
            {typeof prop.bedrooms === 'number' && prop.bedrooms > 0 && (
              <span className="flex items-center gap-2"><BedDouble size={16} className="text-emerald-300" />{prop.bedrooms} bed{prop.bedrooms === 1 ? '' : 's'}</span>
            )}
            {prop.area && (
              <span className="flex items-center gap-2"><MapPin size={16} className="text-emerald-300" />{prop.area}</span>
            )}
            {prop.furnished && prop.furnished.toLowerCase() !== 'unknown' && (
              <span className="flex items-center gap-2"><Sofa size={16} className="text-emerald-300" />{prop.furnished}</span>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_300px] gap-10">
          {/* Image + description */}
          <article data-testid="property-detail-body">
            {prop.image_url && (
              <img
                src={resolveImg(prop.image_url)}
                alt={prop.title}
                className="w-full rounded-xl mb-6"
                onError={(e) => { if (!e.target.dataset.f) { e.target.dataset.f = '1'; e.target.src = `${BACKEND_URL}${FALLBACK_IMAGE}`; } }}
              />
            )}
            {prop.image_url_2 && (
              <img
                src={resolveImg(prop.image_url_2)}
                alt={`${prop.title} – additional view`}
                className="w-full rounded-xl mb-6"
                onError={(e) => (e.target.style.display = 'none')}
              />
            )}
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: safeDesc }} />

            <div className="mt-8 p-5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
              <p className="font-semibold mb-1">Listing aggregated from {prop.source_site || 'a third-party agent'}</p>
              <p className="text-amber-800">
                We pull rental listings from across the web so you don't have to. Please verify all details (price, availability, condition) directly with the agent or landlord before viewing.
              </p>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Property details</p>
              <dl className="space-y-3 text-sm">
                {prop.address && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><MapPin size={12} />Address</dt>
                    <dd className="text-slate-900 font-medium">{prop.address}</dd>
                  </div>
                )}
                {typeof prop.bedrooms === 'number' && prop.bedrooms > 0 && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><BedDouble size={12} />Bedrooms</dt>
                    <dd className="text-slate-900 font-medium">{prop.bedrooms}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-slate-500 flex items-center gap-1.5"><Home size={12} />Type</dt>
                  <dd className="text-slate-900 font-medium capitalize">{prop.property_type}</dd>
                </div>
                {prop.agent && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><Building2 size={12} />Listed by</dt>
                    <dd className="text-slate-900 font-medium">{prop.agent}</dd>
                  </div>
                )}
                {prop.contact_phone && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><Phone size={12} />Phone</dt>
                    <dd className="text-slate-900 font-medium">
                      <a href={`tel:${prop.contact_phone}`} className="hover:text-emerald-600">{prop.contact_phone}</a>
                    </dd>
                  </div>
                )}
                {prop.contact_email && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><Mail size={12} />Email</dt>
                    <dd className="text-slate-900 font-medium">
                      <a href={`mailto:${prop.contact_email}`} className="hover:text-emerald-600 break-all">{prop.contact_email}</a>
                    </dd>
                  </div>
                )}
                {prop.available_from && (
                  <div>
                    <dt className="text-slate-500 flex items-center gap-1.5"><Calendar size={12} />Available</dt>
                    <dd className="text-slate-900 font-medium">{new Date(prop.available_from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</dd>
                  </div>
                )}
              </dl>

              {prop.listing_url && (
                <a
                  href={prop.listing_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="property-detail-cta"
                >
                  View original listing <ExternalLink size={14} />
                </a>
              )}
              <button
                type="button"
                onClick={handleShare}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 px-4 py-2.5 rounded-lg font-medium transition-colors"
                data-testid="property-share-btn"
              >
                <Share2 size={14} /> Share
              </button>

              {/* Social share intents */}
              {(() => {
                const shareUrl = `https://paisley.org.uk/properties/${prop.slug}`;
                const shareText = `${prop.title} — ${fmtPrice(prop.monthly_rent)} | Paisley.org.uk`;
                const intents = [
                  {
                    name: 'Facebook',
                    icon: Facebook,
                    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                    testid: 'property-share-facebook',
                  },
                  {
                    name: 'X',
                    icon: Twitter,
                    href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
                    testid: 'property-share-twitter',
                  },
                  {
                    name: 'WhatsApp',
                    icon: MessageCircle,
                    href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
                    testid: 'property-share-whatsapp',
                  },
                  {
                    name: 'Email',
                    icon: Mail,
                    href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`Spotted this on paisley.org.uk:\n\n${prop.title}\n${shareUrl}`)}`,
                    testid: 'property-share-email',
                  },
                ];
                return (
                  <div className="grid grid-cols-4 gap-2 mt-2" data-testid="property-share-intents">
                    {intents.map(({ name, icon: Icon, href, testid }) => (
                      <a
                        key={name}
                        href={href}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        aria-label={`Share on ${name}`}
                        title={`Share on ${name}`}
                        className="inline-flex items-center justify-center py-2 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-emerald-600 hover:border-emerald-300 transition-colors"
                        data-testid={testid}
                      >
                        <Icon size={15} />
                      </a>
                    ))}
                  </div>
                );
              })()}
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertyDetail;
